import { baseApi } from '../baseApi';

import { DispenseLog, UpdateDispenseStateRequest } from './DispenseLog';

const dispenseLogsApiSlice = baseApi
  .enhanceEndpoints({
    addTagTypes: ['DispenseLogs', 'ActiveDispenseLogs'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDispenseLogsForTransaction: builder.query<DispenseLog[], number>({
        query: (transactionId: number) => ({
          url: `/dispense-logs/transaction/${transactionId}`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result, error, transactionId) =>
          result
            ? [...result.map(({ id }) => ({ type: 'DispenseLogs' as const, id })), { type: 'DispenseLogs', id: transactionId }]
            : [{ type: 'DispenseLogs', id: transactionId }],
      }),
      getActiveDispenseLogs: builder.query<DispenseLog[], void>({
        query: () => ({
          url: `/dispense-logs/active`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        keepUnusedDataFor: 0,
        providesTags: (result) =>
          result
            ? [...result.map(({ id }) => ({ type: 'ActiveDispenseLogs' as const, id })), { type: 'ActiveDispenseLogs', id: 'LIST' }]
            : [{ type: 'ActiveDispenseLogs', id: 'LIST' }],
      }),
      updateDispenseLogState: builder.mutation<DispenseLog, { id: number; request: UpdateDispenseStateRequest }>({
        query: ({ id, request }) => ({
          url: `/dispense-logs/${id}/state`,
          method: 'PUT',
          body: request,
          headers: {
            'x-api-version': '1.0',
          },
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'DispenseLogs', id },
          { type: 'ActiveDispenseLogs', id },
          { type: 'ActiveDispenseLogs', id: 'LIST' },
          { type: 'ActiveDispenseLogs', id: 'OUTSTANDING' },
        ],
      }),
      getReadyToServeDispenseLogs: builder.query<DispenseLog[], void>({
        query: () => ({
          url: `/dispense-logs/ready-to-serve`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        keepUnusedDataFor: 0,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'ActiveDispenseLogs' as const, id })),
                { type: 'ActiveDispenseLogs', id: 'READY_TO_SERVE' },
              ]
            : [{ type: 'ActiveDispenseLogs', id: 'READY_TO_SERVE' }],
      }),
      getOutstandingDispenseLogs: builder.query<DispenseLog[], void>({
        query: () => ({
          url: `/dispense-logs/outstanding`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        keepUnusedDataFor: 0,
        providesTags: (result) =>
          result
            ? [...result.map(({ id }) => ({ type: 'ActiveDispenseLogs' as const, id })), { type: 'ActiveDispenseLogs', id: 'OUTSTANDING' }]
            : [{ type: 'ActiveDispenseLogs', id: 'OUTSTANDING' }],
      }),
      acknowledgeDispenseLogVoid: builder.mutation<DispenseLog, number>({
        query: (id: number) => ({
          url: `/dispense-logs/${id}/acknowledge-void`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        invalidatesTags: (result, error, id) => [
          { type: 'DispenseLogs', id },
          { type: 'ActiveDispenseLogs', id },
          { type: 'ActiveDispenseLogs', id: 'LIST' },
          { type: 'ActiveDispenseLogs', id: 'READY_TO_SERVE' },
          { type: 'ActiveDispenseLogs', id: 'OUTSTANDING' },
        ],
      }),
      reprintDispenseLog: builder.mutation<void, number>({
        query: (id: number) => ({
          url: `/dispense-logs/${id}/reprint`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        invalidatesTags: (result, error, id) => [
          { type: 'DispenseLogs', id },
          { type: 'ActiveDispenseLogs', id },
        ],
      }),
    }),
  });

export const {
  useGetDispenseLogsForTransactionQuery,
  useGetActiveDispenseLogsQuery,
  useGetReadyToServeDispenseLogsQuery,
  useGetOutstandingDispenseLogsQuery,
  useUpdateDispenseLogStateMutation,
  useAcknowledgeDispenseLogVoidMutation,
  useReprintDispenseLogMutation,
} = dispenseLogsApiSlice;
